/**
 * Fachada sobre las variables de entorno.
 * ============================================================================
 *
 * ─── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Dos razones, y la segunda importa más que la primera.
 *
 * **1. Contener la frontera con la herramienta de build.**
 *
 * `process.env.REACT_APP_*` es la API de Create React App. Vite usa
 * `import.meta.env.VITE_*`. Con las lecturas repartidas por 39 archivos, migrar
 * de bundler significaba tocarlos todos; con la fachada, es este archivo.
 *
 * Es el mismo patrón que se aplicó a `react-leaflet` en la Fase 4 y al
 * datepicker en la 4.b: **contener la dependencia en un archivo propio es lo
 * que hace barato el cambio, y el momento de hacerlo es antes de necesitarlo**
 * (ADR 0004).
 *
 * **2. Que una variable ausente se oiga.**
 *
 * Ésta es la razón que justifica la fachada aunque nunca se migre a Vite.
 *
 * Con la lectura directa, una variable que falta no falla: se interpola como
 * `undefined` y produce una URL como `undefinedapi/admin/orders/`. La petición
 * sale, el servidor devuelve un error, el reducer de fallo hace su trabajo y la
 * pantalla se queda con los datos anteriores. **Todo «funciona».**
 *
 * Es exactamente la familia de fallos que dominó la Fase 6: cuatro claves de
 * estado mal escritas y cinco peticiones mal acotadas, todas invisibles porque
 * JavaScript no protesta. Aquí se protesta.
 */

/** Lo que la aplicación no puede hacer sin ello. */
const OBLIGATORIAS = {
  baseDominio: 'REACT_APP_BASE_DOMAIN',
  baseApi: 'REACT_APP_BASE_API_URL',
  ws: 'REACT_APP_BASE_WS_URL'
}

/** Lo que puede faltar sin romper nada: se degrada. */
const OPCIONALES = {
  wsTienda: 'REACT_APP_BASE_WS_SHOP_URL',
  tienda: 'REACT_APP_STORE_URL',
  s3: 'REACT_APP_S3_URL',
  webCliente: 'REACT_APP_CLIENT_WEB_URL',
  seguimiento: 'REACT_APP_TRACKER_URL',
  posthogClave: 'REACT_APP_POSTHOG_KEY',
  posthogHost: 'REACT_APP_POSTHOG_HOST',
  crispId: 'REACT_APP_CRISP_ID',
  cartoClave: 'REACT_APP_CARTO_API_KEY'
}

/**
 * El único punto del proyecto que lee `process.env`.
 *
 * Cuando se migre a Vite, esta función pasa a leer `import.meta.env` y **nada
 * más cambia**. Se escribe así, con el nombre completo en cada acceso, porque
 * los empaquetadores sustituyen `process.env.X` de forma literal durante la
 * compilación: un acceso dinámico `process.env[nombre]` **no se sustituiría** y
 * llegaría vacío al navegador.
 */
const leer = nombre =>
  ({
    REACT_APP_BASE_DOMAIN: process.env.REACT_APP_BASE_DOMAIN,
    REACT_APP_BASE_API_URL: process.env.REACT_APP_BASE_API_URL,
    REACT_APP_BASE_WS_URL: process.env.REACT_APP_BASE_WS_URL,
    REACT_APP_BASE_WS_SHOP_URL: process.env.REACT_APP_BASE_WS_SHOP_URL,
    REACT_APP_STORE_URL: process.env.REACT_APP_STORE_URL,
    REACT_APP_S3_URL: process.env.REACT_APP_S3_URL,
    REACT_APP_CLIENT_WEB_URL: process.env.REACT_APP_CLIENT_WEB_URL,
    REACT_APP_TRACKER_URL: process.env.REACT_APP_TRACKER_URL,
    REACT_APP_POSTHOG_KEY: process.env.REACT_APP_POSTHOG_KEY,
    REACT_APP_POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST,
    REACT_APP_CRISP_ID: process.env.REACT_APP_CRISP_ID,
    REACT_APP_CARTO_API_KEY: process.env.REACT_APP_CARTO_API_KEY
  })[nombre]

const construir = () => {
  const valores = {}
  const faltan = []

  Object.entries(OBLIGATORIAS).forEach(([clave, nombre]) => {
    const valor = leer(nombre)
    if (!valor) faltan.push(nombre)
    valores[clave] = valor
  })

  Object.entries(OPCIONALES).forEach(([clave, nombre]) => {
    valores[clave] = leer(nombre) || ''
  })

  if (faltan.length) {
    // No se lanza: dejar la aplicación sin arrancar por una variable ausente es
    // peor que dejarla funcionar a medias con un aviso claro. Pero el aviso
    // tiene que ser imposible de pasar por alto, porque el síntoma sin él es
    // una URL como `undefinedapi/admin/orders/` que nadie relaciona con esto.
    console.error(
      `⚠️ Faltan variables de entorno: ${faltan.join(', ')}.\n` +
        'Las URLs se construirán con `undefined` y las peticiones fallarán ' +
        'sin explicación aparente. Revisa el archivo `.env` del entorno o las ' +
        'variables de la consola de Amplify.'
    )
  }

  return valores
}

const entorno = {
  ...construir(),

  /** Modo de ejecución. `NODE_ENV` lo fija la herramienta, no un `.env`. */
  esProduccion: process.env.NODE_ENV === 'production',
  esDesarrollo: process.env.NODE_ENV === 'development',
  esPrueba: process.env.NODE_ENV === 'test',

  /** Ruta pública de los estáticos. La usan el service worker y los assets. */
  rutaPublica: process.env.PUBLIC_URL || ''
}

export default entorno
